import { Subject, Observable } from 'rxjs';
import * as faye from 'faye';
import { IRealtimeClient } from './realtime-client.interface';
import { Mutex } from 'async-mutex';

type FayeExtensionFn = (tokens?: {
  [key: string]: string;
}) => (message: any, next: (message: any) => void) => void;

export class FayeClient implements IRealtimeClient {
  private client: any;
  private _fayeDisconnected$ = new Subject<boolean>();
  private tokens: { [key: string]: string } = {};
  private mutex = new Mutex();
  private isFirstConnection = true;
  private MAX_RETRY_ATTEMPTS = 5;
  private EXP_BACKOFF_SCALING_FACTOR = 200;

  constructor(
    url: string,
    extensions?: {
      ingoing?: FayeExtensionFn;
      outgoing?: FayeExtensionFn;
    }
  ) {
    this.client = new faye.Client(url, {});
    this.client.disable('autodisconnect');

    this.client.on('transport:down', () => {
      console.log('faye disconnected');
      this._fayeDisconnected$.next(true);
    });

    this.client.on('transport:up', async () => {
      if (this.isFirstConnection) {
        this.isFirstConnection = false;
        console.log('faye connected');
      } else {
        console.log('faye reconnected');
      }
      this._fayeDisconnected$.next(false);
    });

    const fayeExtensions: any = {};
    if (extensions?.ingoing) {
      fayeExtensions.ingoing = extensions.ingoing(this.tokens);
    }
    if (extensions?.outgoing) {
      fayeExtensions.outgoing = extensions.outgoing(this.tokens);
    }
    this.client.addExtension(fayeExtensions);
  }

  unsubscribeFromChannel(channel: string): void {
    this.client.unsubscribe(channel);
    console.log(`Faye unsubscribed from ${channel}`);
  }

  async subscribeToChannel(
    channel: string,
    token: string,
    callback: (message: any) => void
  ): Promise<void> {
    try {
      // storing token
      this.tokens[channel] = token;
      await this._subscribeWithRetry(channel, callback);
      console.log(`faye successfully subscribed to ${channel}`);
    } catch (e) {
      console.error(`faye failed to subscribe to ${channel}: ${e}`);
      throw e;
    }
  }

  private async _subscribeWithRetry(
    channel: string,
    callback: (message: any) => void
  ): Promise<void> {
    for (let attempt = 0; attempt < this.MAX_RETRY_ATTEMPTS; attempt++) {
      const release = await this.mutex.acquire();

      try {
        await this.client.subscribe(channel, callback);
        release();
        break;
      } catch (err) {
        release();

        console.error(
          `faye failed to subscribe to ${channel}. attempt ${attempt + 1} of ${
            this.MAX_RETRY_ATTEMPTS
          }`,
          err
        );

        if (attempt === this.MAX_RETRY_ATTEMPTS - 1) {
          throw err;
        }

        const backOffTime =
          Math.pow(2, attempt) * this.EXP_BACKOFF_SCALING_FACTOR;
        await this._sleep(backOffTime);
      }
    }
  }

  async publish(channel: string, _token: string, message: any): Promise<void> {
    await this.client.publish(channel, message);
  }

  realtimeClientDisconnected$(): Observable<boolean> {
    return this._fayeDisconnected$.asObservable();
  }

  disconnect(): void {
    this.client.disconnect();
  }

  private _sleep(time: number) {
    return new Promise<void>((resolve) => {
      setTimeout(resolve, time);
    });
  }
}
